import {firstMonthMajor} from "upsell/firstMonthPricing";
import {formatMoney} from "pricing/PricingStorage";
import {PAID_PLAN_IDS, type BillingPeriod, type PlanId} from "pricing/planCatalogue";
import type {PlanCardModel, ResolvedPricing} from "pricing/pricingPlans";

/**
 * The discounted first invoice a live ladder rung buys, written onto the plan cards
 * (kitecorp/transferify-ui#1977).
 *
 * React-free, and separate from the rest of the ladder rules because it answers a different question:
 * {@link ./ladderOffer} decides WHETHER there is an offer, this decides what number appears on a card
 * when there is. The arithmetic itself is not restated here — {@link firstMonthMajor} already matches
 * Stripe's order of operations (percentage of the amount, discount rounded to a whole minor unit,
 * subtracted), and a second spelling of it is how a page quotes a price a cent off the one charged.
 *
 * <b>MONTHLY only.</b> A rung's code discounts the first invoices of a monthly subscription; writing a
 * halved figure under a yearly card would promise a discount the checkout would not attach. The
 * yearly period is therefore left exactly as the catalogue built it rather than given a note of its
 * own.
 *
 * <b>The percentage is the api's.</b> Nothing here chooses or defaults a discount, and a percentage
 * that cannot produce an honest figure leaves the card at its plain price — the same "say less rather
 * than say wrong" direction {@link firstMonthMajor} takes.
 *
 * <b>A card whose CTA belongs to another offer is left alone</b> (`offeredElsewhere`,
 * kitecorp/transferify-ui#1993). The two offers first met on `/pricing` when the referred friend's gift
 * arrived: the gift SWAPS a claimable tier's button, so writing this rung's first invoice under it would
 * put one price in the note and charge a different one on the press. A note that does not describe its
 * own button is worse than no note, so the tier keeps its plain price and the gift states its own.
 */
export function withLadderFirstMonth(
	plans: readonly PlanCardModel[],
	period: BillingPeriod,
	pricing: ResolvedPricing,
	discount: {percentOff: number; months: number} | null,
	locale: string,
	note: (price: string, months: number) => string,
	/** Tiers whose CTA another offer has taken over, so this rung must not price them. */
	offeredElsewhere: readonly PlanId[] = [],
): PlanCardModel[] {
	if (discount === null || period !== "MONTHLY") {
		return [...plans];
	}
	return plans.map((plan) => {
		const tier = PAID_PLAN_IDS.find(id => id === plan.id);
		if (tier === undefined || offeredElsewhere.includes(plan.id)) {
			return plan;
		}
		const discounted = firstMonthMajor(pricing[tier].monthly, pricing.currency, discount.percentOff);
		if (discounted === null) {
			return plan;
		}
		return {
			...plan,
			note: note(formatMoney(pricing.currency, discounted, locale), discount.months),
		};
	});
}
